import { minutesToMilliseconds } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { convertBidsAsksToDepthData } from '../lib/depthDataHelpers';
import { captureError } from '../lib/errorTracking';
import { omit } from '../lib/objectHelpers';
import { checkCrossedOrderBook, mergeBidsAsks } from '../lib/orderbookHelpers';
import { BidsAsks, BookDepth } from '../types/orderbook';
import { useBookDepth, useWebsocketStore } from './useWebsocketStore';

const REFRESH_COOLDOWN_MS = minutesToMilliseconds(30);

type ProductId = string;

interface MarketLiquidityState {
  liquidityByProduct: Record<ProductId, BidsAsks>;
  lastProcessedTimestamp: Record<ProductId, number>;
  lastRefreshTimestamp: Record<ProductId, number>;
  errorByProduct: Record<ProductId, string | null>;

  // Actions
  updateLiquidity: (productId: ProductId, data: BidsAsks, timestamp: number) => void;
  clearLiquidity: (productId: ProductId) => void;
  setRefreshTimestamp: (productId: ProductId, timestamp: number) => void;
  setError: (productId: ProductId, error: string | null) => void;
}

export const useMarketLiquidityStore = create<MarketLiquidityState>()((set) => ({
  liquidityByProduct: {},
  lastProcessedTimestamp: {},
  lastRefreshTimestamp: {},
  errorByProduct: {},

  updateLiquidity: (productId, newData, timestamp) => {
    set((state) => {
      const mergedData = mergeBidsAsks(
        state.liquidityByProduct[productId] || { bids: [], asks: [] },
        newData
      );

      // Check if the merged data is valid (not crossed)
      const crossedCheck = checkCrossedOrderBook(mergedData);

      return {
        liquidityByProduct: {
          ...state.liquidityByProduct,
          [productId]: mergedData,
        },
        lastProcessedTimestamp: {
          ...state.lastProcessedTimestamp,
          [productId]: timestamp,
        },
        // Only clear error if the new data is valid (not crossed)
        errorByProduct: {
          ...state.errorByProduct,
          [productId]: crossedCheck.isCrossed ? state.errorByProduct[productId] || null : null,
        },
      };
    });
  },

  clearLiquidity: (productId) => {
    set((state) => ({
      liquidityByProduct: omit(state.liquidityByProduct, productId),
      lastProcessedTimestamp: omit(state.lastProcessedTimestamp, productId),
      lastRefreshTimestamp: omit(state.lastRefreshTimestamp, productId),
      errorByProduct: omit(state.errorByProduct, productId),
    }));
  },

  setRefreshTimestamp: (productId, timestamp) => {
    set((state) => ({
      lastRefreshTimestamp: {
        ...state.lastRefreshTimestamp,
        [productId]: timestamp,
      },
    }));
  },

  setError: (productId, error) => {
    set((state) => ({
      errorByProduct: {
        ...state.errorByProduct,
        [productId]: error,
      },
    }));
  },
}));

export const useMarketLiquidity = (productId: string | undefined) => {
  const updateLiquidity = useMarketLiquidityStore((s) => s.updateLiquidity);
  const clearLiquidity = useMarketLiquidityStore((s) => s.clearLiquidity);
  const liquidityByProduct = useMarketLiquidityStore((s) => s.liquidityByProduct);
  const lastProcessedTimestampByProduct = useMarketLiquidityStore((s) => s.lastProcessedTimestamp);
  const lastRefreshTimestampByProduct = useMarketLiquidityStore((s) => s.lastRefreshTimestamp);
  const errorByProduct = useMarketLiquidityStore((s) => s.errorByProduct);
  const setRefreshTimestamp = useMarketLiquidityStore((s) => s.setRefreshTimestamp);
  const setError = useMarketLiquidityStore((s) => s.setError);
  const refreshBookDepth = useWebsocketStore((s) => s.refreshBookDepth);

  // Memoize the handler to prevent unnecessary re-subscriptions and infinite loops
  const handleBookDepthUpdate = useCallback(
    (data: BookDepth | null) => {
      if (!productId) return;
      if (data === null) {
        clearLiquidity(productId);
        return;
      }
      const newData = {
        asks: data.asks || [],
        bids: data.bids || [],
      };

      updateLiquidity(data.productId, newData, data.timestamp);
    },
    [clearLiquidity, productId, updateLiquidity]
  );

  // Handle WebSocket subscription using our new hook with memoized handler
  useBookDepth(productId, handleBookDepthUpdate);

  // Check for crossed order book (corrupted data)
  useEffect(() => {
    if (!productId || !liquidityByProduct[productId]) return;

    const orderBook = liquidityByProduct[productId];
    const crossedCheck = checkCrossedOrderBook(orderBook);

    if (crossedCheck.isCrossed) {
      const now = Date.now();
      const lastRefresh = lastRefreshTimestampByProduct[productId] || 0;
      const timeSinceLastRefresh = now - lastRefresh;

      // If we already tried to refresh recently, just show error without retrying
      if (timeSinceLastRefresh < REFRESH_COOLDOWN_MS && lastRefresh > 0) {
        const errorMessage = `Order book data is corrupted: best bid (${crossedCheck.bestBidPrice}) >= best ask (${crossedCheck.bestAskPrice})`;
        setError(productId, errorMessage);
        captureError(
          new Error(`[MarketLiquidity] ${errorMessage} for ${productId} after refresh attempt`)
        );
      } else {
        // First time seeing crossed book - try refreshing once
        const message =
          `[MarketLiquidity] Crossed order book detected for ${productId}: ` +
          `best bid ${crossedCheck.bestBidPrice} >= best ask ${crossedCheck.bestAskPrice}. Refreshing order book.`;
        captureError(new Error(message));

        setRefreshTimestamp(productId, now);
        refreshBookDepth(productId);
      }
    }
  }, [
    liquidityByProduct,
    productId,
    refreshBookDepth,
    lastRefreshTimestampByProduct,
    setError,
    setRefreshTimestamp,
  ]);

  const liquidity = productId ? liquidityByProduct[productId] : undefined;
  const lastTimestamp = productId ? lastProcessedTimestampByProduct[productId] : undefined;
  const error = productId ? errorByProduct[productId] : undefined;

  return useMemo(() => {
    if (!productId) {
      return {
        liquidityData: undefined,
        marketDepth: undefined,
        lastProcessedTimestamp: 0,
        error: null,
      };
    }

    return {
      liquidityData: liquidity,
      marketDepth: liquidity ? convertBidsAsksToDepthData(liquidity) : undefined,
      lastProcessedTimestamp: lastTimestamp || 0,
      error: error || null,
    };
  }, [productId, liquidity, lastTimestamp, error]);
};
